import React, { useRef, useEffect, useCallback } from 'react';
import { VEDA_ONLINE_PRESENTER_BASE } from '../../../constants/countryDashboard';
import { useCommentsPanel } from '../../../hooks/useCommentsPanel';
import { usePresenterComments } from '../../../hooks/usePresenterComments';
import { usePresenterFullscreen } from '../../../hooks/usePresenterFullscreen';
import PresenterHeader from './PresenterHeader';
import PresenterIframePane from './PresenterIframePane';
import PresenterCommentsPanel from './comments/PresenterCommentsPanel';

function PresenterModal({ activePresenterView, onClose }) {
  const modalShellRef = useRef(null);
  const presenterIframeRef = useRef(null);

  const {
    isOpen: commentsOpen,
    isPinned,
    togglePanel,
    closePanel,
    togglePin,
    pinPanel,
    resetPanel,
    handlePanelMouseEnter,
    handlePanelMouseLeave,
    handleCommentsButtonMouseEnter,
  } = useCommentsPanel();

  const { isFullscreen, toggleFullscreen } = usePresenterFullscreen({
    modalShellRef,
    iframeRef: presenterIframeRef,
    isPresenterOpen: !!activePresenterView,
    commentsOpen,
    isPinned,
    pinPanel,
  });

  const savedViewId = activePresenterView?.viewId ?? null;
  const { comments, loading, error } = usePresenterComments(savedViewId, commentsOpen);

  useEffect(() => {
    if (!activePresenterView) {
      resetPanel();
    }
  }, [activePresenterView, resetPanel]);

  const handleClosePresenter = useCallback(() => {
    resetPanel();
    onClose();
  }, [resetPanel, onClose]);

  if (!activePresenterView) {
    return null;
  }

  const presenterTitle = activePresenterView.name || 'VedaOnline presenter view';
  const presenterSrc = `${VEDA_ONLINE_PRESENTER_BASE}${encodeURIComponent(activePresenterView.id)}`;
  const pinnedLayout = commentsOpen && isPinned;

  return (
    <div className="fixed inset-0 z-[12000] box-border flex flex-col bg-black/60 p-3 sm:p-4 md:p-5">
      <div
        ref={modalShellRef}
        className="vs-element-fullscreen bg-white rounded-lg shadow-xl flex flex-1 flex-col min-h-0 min-w-0 w-full overflow-hidden"
      >
        <PresenterHeader
          title={presenterTitle}
          viewId={activePresenterView.id}
          commentsOpen={commentsOpen}
          isFullscreen={isFullscreen}
          onToggleFullscreen={toggleFullscreen}
          onToggleComments={togglePanel}
          onCommentsButtonMouseEnter={handleCommentsButtonMouseEnter}
          onClose={handleClosePresenter}
        />
        <div
          className={[
            'flex-1 min-h-0 relative bg-gray-50',
            pinnedLayout ? 'flex flex-row' : '',
          ].join(' ')}
        >
          <PresenterIframePane
            iframeRef={presenterIframeRef}
            title={presenterTitle}
            src={presenterSrc}
            isPinnedLayout={pinnedLayout}
          />
          {commentsOpen && (
            <PresenterCommentsPanel
              isPinned={isPinned}
              loading={loading}
              error={error}
              comments={comments}
              onTogglePin={togglePin}
              onClose={closePanel}
              onMouseEnter={handlePanelMouseEnter}
              onMouseLeave={handlePanelMouseLeave}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default React.memo(PresenterModal);
